/**
 * Web copy of crx-be/src/utils/ArabicText.ts (keep the two in step).
 *
 * Arabic in, English out (user, 2026-10-15: "answers should always be in
 * English … if I shared a contact name in Arabic, when you fill add the name
 * in English"). Code only, no AI, so names never leave CRX:
 *
 *   - westernDigits:   ٠٥٠ ١٢٣ ٤٥٦٧ → 050 123 4567
 *   - arabicNameToEnglish:    محمد أحمد الكعبي → Mohammed Ahmed Al Kaabi
 *   - arabicCompanyToEnglish: شركة الوادي للخدمات الأمنية ذ.م.م → Al Wadi Security Services LLC
 *   - arabicPlaceToEnglish:   مسقط، عمان → Muscat, Oman
 *   - arabicSkeleton / latinSkeleton: the consonants a name is spelt with in
 *     either script (محمد ≈ Mohammed ≈ Muhammad), so the masking layer can
 *     find CRX's English names written in Arabic.
 *
 * Arabic leaves out short vowels, so one name has several English spellings:
 * the lists hold CRX's house spelling first, then the common variants. Names
 * not on the lists are spelt by rule — always shown as "check spelling".
 */

const ARABIC_LETTER = /[ء-يٱ-ۓۺ-ۿ]/;
const ARABIC_ANY = /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/;

/** Whether the text has Arabic letters. */
export const hasArabic = (text: unknown): boolean => ARABIC_ANY.test(String(text ?? ""));

/** Arabic-Indic and Persian digits (and Arabic decimal/thousands marks) as 0–9, "." and ","; a lone ٠ between spaces is a dot. */
export const westernDigits = (text: unknown): string =>
    String(text ?? "")
        // a lone "٠" between spaces is a dot OCR misread on a card ("Tel 2456 7890 ٠ Mob 9123 4567"), not a zero
        .replace(/(?<=\s)\u0660(?=\s)/g, "·")
        .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
        .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06F0))
        .replace(/٫/g, ".")
        .replace(/٬/g, ",");

/** Diacritics and the tatweel off (the letters stay as written). */
const plainArabic = (text: string): string => text.replace(/[ً-ٰٟۖ-ۭـ]/g, "");

/**
 * The form Arabic words are compared in: no diacritics or tatweel, every alef
 * as ا, ى as ي, ة as ه, ؤ as و, ئ as ي.
 */
export const foldArabic = (text: unknown): string =>
    plainArabic(westernDigits(text))
        .replace(/[أإآٱ]/g, "ا")
        .replace(/ى/g, "ي")
        .replace(/ة/g, "ه")
        .replace(/ؤ/g, "و")
        .replace(/ئ/g, "ي")
        .replace(/\s+/g, " ")
        .trim();

// --- names -----------------------------------------------------------------

/** Given names: Arabic → [house spelling, …variants]. */
const GIVEN_NAMES: Record<string, string[]> = {
    "محمد": ["Mohammed", "Muhammad", "Mohamed", "Mohammad", "Mohamad", "Muhammed"],
    "أحمد": ["Ahmed", "Ahmad"], "محمود": ["Mahmoud", "Mahmood", "Mahmud"], "مصطفى": ["Mustafa", "Mostafa", "Moustafa"],
    "علي": ["Ali"], "عمر": ["Omar", "Umar"], "عمرو": ["Amr"], "عثمان": ["Othman", "Osman", "Uthman"],
    "حسن": ["Hassan", "Hasan"], "حسين": ["Hussein", "Hussain", "Husain", "Hossein"], "خالد": ["Khaled", "Khalid"],
    "خليل": ["Khalil"], "إبراهيم": ["Ibrahim", "Ebrahim"], "إسماعيل": ["Ismail", "Ismael"], "يوسف": ["Youssef", "Yousef", "Yusuf", "Yousif"],
    "يعقوب": ["Yacoub", "Yaqoub", "Yakub"], "يحيى": ["Yahya"], "يونس": ["Younes", "Younis", "Yunus"], "موسى": ["Mousa", "Musa", "Moussa"],
    "عيسى": ["Issa", "Eisa", "Isa"], "سليمان": ["Suleiman", "Sulaiman", "Soliman"], "داود": ["Dawood", "Daoud", "Dawoud"], "آدم": ["Adam"],
    "زكريا": ["Zakaria", "Zakariya"], "سامي": ["Sami", "Samy"], "رامي": ["Rami", "Ramy"], "فادي": ["Fadi", "Fady"], "هاني": ["Hani", "Hany"],
    "نبيل": ["Nabil", "Nabeel"], "وليد": ["Walid", "Waleed"], "طارق": ["Tariq", "Tarek", "Tareq"], "زياد": ["Ziad", "Ziyad"],
    "سعيد": ["Saeed", "Said", "Saeid"], "سالم": ["Salem", "Salim"], "سليم": ["Salim", "Selim"], "صالح": ["Saleh", "Salih"],
    "سلطان": ["Sultan"], "راشد": ["Rashid", "Rashed"], "رشيد": ["Rasheed", "Rashid"], "حمد": ["Hamad"], "حمدان": ["Hamdan"],
    "ماجد": ["Majid", "Majed"], "فيصل": ["Faisal", "Faysal"], "فهد": ["Fahad", "Fahd"], "ناصر": ["Nasser", "Naser", "Nasir"],
    "جاسم": ["Jassim", "Jasim", "Jasem"], "منصور": ["Mansour", "Mansoor"], "مروان": ["Marwan"], "بلال": ["Bilal", "Belal"],
    "أنس": ["Anas"], "أسامة": ["Osama", "Usama", "Usamah"], "حمزة": ["Hamza", "Hamzah"], "كريم": ["Karim", "Kareem"],
    "عادل": ["Adel", "Adil"], "عامر": ["Amer", "Aamer"], "أمير": ["Amir", "Ameer"], "أيمن": ["Ayman"], "بسام": ["Bassam"],
    "عماد": ["Emad", "Imad"], "عصام": ["Essam", "Issam"], "فوزي": ["Fawzi", "Fawzy"], "غسان": ["Ghassan"], "هيثم": ["Haitham", "Haytham"],
    "هشام": ["Hisham", "Hesham"], "إياد": ["Iyad", "Eyad"], "جمال": ["Jamal", "Gamal"], "كمال": ["Kamal"], "لؤي": ["Louay", "Luay"],
    "ماهر": ["Maher"], "مازن": ["Mazen"], "مهند": ["Mohanad", "Muhannad"], "نادر": ["Nader"], "نزار": ["Nizar"], "قيس": ["Qais", "Kais"],
    "رائد": ["Raed"], "رياض": ["Riyad", "Riad"], "سامر": ["Samer"], "شادي": ["Shadi", "Shady"], "سهيل": ["Suhail", "Sohail"],
    "طلال": ["Talal"], "ثامر": ["Thamer"], "وائل": ["Wael"], "يزن": ["Yazan"], "زيد": ["Zaid"], "زايد": ["Zayed"],
    "عبدالله": ["Abdullah", "Abdallah", "Abdulla"], "عبدالرحمن": ["Abdulrahman", "Abdul Rahman", "Abdelrahman", "Abdalrahman"],
    "عبدالعزيز": ["Abdulaziz", "Abdul Aziz", "Abdelaziz"], "سعد": ["Saad"], "سعود": ["Saud"], "تركي": ["Turki"], "بندر": ["Bandar"],
    "نايف": ["Naif", "Nayef"], "مشعل": ["Mishal", "Meshal"], "متعب": ["Miteb"], "عبيد": ["Obaid", "Ubaid"], "حميد": ["Humaid", "Hamid"],
    "حامد": ["Hamed", "Hamid"], "مبارك": ["Mubarak"], "خلفان": ["Khalfan"], "سيف": ["Saif"], "هزاع": ["Hazza"], "ذياب": ["Theyab", "Diab"],
    "جابر": ["Jaber", "Jabir"], "صباح": ["Sabah"], "تميم": ["Tamim", "Tameem"], "علاء": ["Alaa", "Ala"], "ضياء": ["Diaa", "Dia"],
    "بهاء": ["Bahaa"], "إيهاب": ["Ehab", "Ihab"], "أشرف": ["Ashraf"], "أكرم": ["Akram"], "أسعد": ["Asaad", "Asad"], "باسل": ["Basel", "Basil"],
    "باسم": ["Basem", "Basim"], "بشار": ["Bashar"], "بشير": ["Bashir", "Basheer"], "جهاد": ["Jihad"], "حازم": ["Hazem"], "حاتم": ["Hatem"],
    "حسام": ["Hussam", "Hossam"], "خضر": ["Khader", "Khidr"], "راضي": ["Radi"], "ربيع": ["Rabie", "Rabia"], "رضا": ["Reda", "Rida"],
    "زهير": ["Zuhair", "Zuhir"], "سمير": ["Samir", "Sameer"], "شريف": ["Sharif", "Sherif"], "صلاح": ["Salah"], "صهيب": ["Suhaib", "Sohaib"],
    "عاطف": ["Atef"], "عدنان": ["Adnan"], "عزيز": ["Aziz"], "عوني": ["Awni"], "غازي": ["Ghazi"], "فراس": ["Firas", "Feras"],
    "فريد": ["Farid"], "فؤاد": ["Fouad", "Fuad"], "قاسم": ["Qasim", "Kassem", "Qassem"], "مجدي": ["Majdi", "Magdi"], "محسن": ["Mohsen", "Muhsin"],
    "مراد": ["Murad", "Mourad"], "معاذ": ["Muath", "Moaz", "Muaz"], "منير": ["Munir", "Mounir"], "مهدي": ["Mahdi", "Mehdi"], "ناجي": ["Naji"],
    "نصر": ["Nasr"], "نعيم": ["Naim", "Naeem"], "هادي": ["Hadi"], "وسيم": ["Wassim", "Waseem"], "ياسر": ["Yasser", "Yasir"],
    "ياسين": ["Yassin", "Yaseen"], "يزيد": ["Yazid"], "أنور": ["Anwar"], "إسحاق": ["Ishaq", "Ishak"], "إلياس": ["Elias", "Ilyas"],
    "جورج": ["George"], "رامز": ["Ramez"], "نضال": ["Nidal"], "إسلام": ["Islam"], "مؤمن": ["Moamen", "Mumin"], "طه": ["Taha"],
    "فارس": ["Fares", "Faris"], "ليث": ["Laith", "Layth"], "أوس": ["Aws"], "أيوب": ["Ayoub", "Ayyub"], "ثائر": ["Thaer"], "جواد": ["Jawad"],
    "حيدر": ["Haider", "Haidar"], "رعد": ["Raad"], "سنان": ["Sinan"], "عباس": ["Abbas"], "عمار": ["Ammar"], "قصي": ["Qusai", "Qusay"],
    "كاظم": ["Kazem", "Kadhim"], "مالك": ["Malek", "Malik"], "نواف": ["Nawaf"], "ريان": ["Rayan", "Ryan"], "إياس": ["Iyas"],
    "حنا": ["Hanna"], "ميشيل": ["Michel"], "بطرس": ["Boutros"], "جريس": ["Jiries"], "نقولا": ["Nicola"], "شخبوط": ["Shakhbout"],
    "طحنون": ["Tahnoun"], "مكتوم": ["Maktoum"], "نهيان": ["Nahyan"], "ثاني": ["Thani"], "جوعان": ["Joaan"], "حمود": ["Hamoud"],
    "فاطمة": ["Fatima", "Fatma", "Fatimah"], "عائشة": ["Aisha", "Aysha", "Ayesha"], "مريم": ["Maryam", "Mariam", "Miriam"],
    "سارة": ["Sara", "Sarah"], "ليلى": ["Layla", "Laila", "Leila"], "نور": ["Noor", "Nour", "Nur"], "هدى": ["Huda", "Hoda"],
    "هالة": ["Hala"], "رنا": ["Rana"], "رانيا": ["Rania", "Ranya"], "لينا": ["Lina", "Leena"], "دينا": ["Dina", "Deena"], "ريم": ["Reem", "Rim"],
    "نادية": ["Nadia", "Nadya"], "سلمى": ["Salma"], "سميرة": ["Samira", "Sameera"], "أمل": ["Amal"], "أسماء": ["Asma", "Asmaa"],
    "حنان": ["Hanan"], "إيمان": ["Iman", "Eman"], "منى": ["Mona", "Muna"], "ربى": ["Ruba", "Roba"], "رقية": ["Ruqaya", "Roqaya"],
    "زينب": ["Zainab", "Zeinab"], "خديجة": ["Khadija", "Khadeeja"], "هبة": ["Hiba", "Heba"], "دانة": ["Dana"], "لمى": ["Lama"],
    "شيماء": ["Shaima", "Shaimaa"], "وفاء": ["Wafa", "Wafaa"], "ياسمين": ["Yasmin", "Yasmeen"], "جمانة": ["Jumana", "Joumana"],
    "رزان": ["Razan"], "روان": ["Rawan"], "هيا": ["Haya"], "نوال": ["Nawal"], "سعاد": ["Suad", "Souad"], "عبير": ["Abeer", "Abir"],
    "غادة": ["Ghada"], "لبنى": ["Lubna"], "مها": ["Maha"], "ميساء": ["Maysa", "Maisaa"], "نسرين": ["Nisreen", "Nesreen"], "هند": ["Hind"],
    "وعد": ["Waad"], "آمنة": ["Amna", "Amina"], "بشرى": ["Bushra"], "تسنيم": ["Tasneem"], "جنى": ["Jana"], "دعاء": ["Doaa", "Duaa"],
    "رهف": ["Rahaf"], "سجى": ["Saja"], "شهد": ["Shahad"], "علياء": ["Alia", "Aliaa"], "فرح": ["Farah"], "لانا": ["Lana"], "مي": ["Mai", "May"],
    "ندى": ["Nada"], "نهى": ["Noha", "Nuha"], "هديل": ["Hadeel"], "يارا": ["Yara"], "ريما": ["Rima", "Reema"], "سلوى": ["Salwa"],
    "عفاف": ["Afaf"], "كوثر": ["Kawthar"], "منار": ["Manar"], "نجلاء": ["Najla"], "إسراء": ["Israa", "Esraa"], "آية": ["Aya", "Ayah"],
    "لجين": ["Lujain"], "رغد": ["Raghad"], "ملك": ["Malak"], "جود": ["Jood"], "تالا": ["Tala"], "ماري": ["Mary"], "ريتا": ["Rita"],
};

/** Family and tribe names: Arabic → [house spelling, …variants]. */
const FAMILY_NAMES: Record<string, string[]> = {
    "الكعبي": ["Al Kaabi"], "المنصوري": ["Al Mansouri"], "الشامسي": ["Al Shamsi"], "الظاهري": ["Al Dhaheri", "Al Zaheri"], "النعيمي": ["Al Nuaimi"],
    "المهيري": ["Al Mheiri"], "الهاشمي": ["Al Hashimi", "Al Hashemi"], "المري": ["Al Marri"], "الفلاسي": ["Al Falasi"], "القاسمي": ["Al Qasimi"],
    "الزعابي": ["Al Zaabi"], "السويدي": ["Al Suwaidi"], "الكتبي": ["Al Ketbi"], "البلوشي": ["Al Balushi"], "الحمادي": ["Al Hammadi"],
    "الرميثي": ["Al Romaithi"], "المزروعي": ["Al Mazrouei"], "العامري": ["Al Ameri"], "الشحي": ["Al Shehhi"], "النقبي": ["Al Naqbi"],
    "الحوسني": ["Al Hosani"], "اليماحي": ["Al Yammahi"], "الجنيبي": ["Al Junaibi"], "البادي": ["Al Badi"], "الحبسي": ["Al Habsi"],
    "الغفلي": ["Al Ghafli"], "المنهالي": ["Al Manhali"], "القبيسي": ["Al Qubaisi"], "العلي": ["Al Ali"], "الملا": ["Al Mulla"],
    "الأنصاري": ["Al Ansari"], "البستكي": ["Al Bastaki"], "الجابري": ["Al Jaberi"], "الراشدي": ["Al Rashdi"], "الشكيلي": ["Al Shukaili"],
    "البوسعيدي": ["Al Busaidi"], "الحارثي": ["Al Harthi"], "الهنائي": ["Al Hinai"], "العبري": ["Al Abri"], "السعدي": ["Al Saadi"],
    "الرواحي": ["Al Rawahi"], "الكندي": ["Al Kindi"], "المعمري": ["Al Maamari"], "العجمي": ["Al Ajmi"], "الرشيدي": ["Al Rashidi"],
    "العازمي": ["Al Azmi"], "الهاجري": ["Al Hajri"], "الكواري": ["Al Kuwari"], "السليطي": ["Al Sulaiti"], "المهندي": ["Al Mohannadi"],
    "العطية": ["Al Attiyah"], "الخليفي": ["Al Khulaifi"], "السادة": ["Al Sada"], "الزياني": ["Al Zayani"], "الخياط": ["Al Khayyat"],
    "القرشي": ["Al Qurashi"], "الأيوبي": ["Al Ayoubi"], "النابلسي": ["Al Nabulsi"], "الخليلي": ["Al Khalili"], "الحلبي": ["Al Halabi"],
    "البغدادي": ["Al Baghdadi"], "الموصلي": ["Al Mosuli"], "البصري": ["Al Basri"], "الحسيني": ["Al Husseini"], "الخالدي": ["Al Khalidi"],
    "الدجاني": ["Al Dajani"], "البرغوثي": ["Barghouti", "Al Barghouti"], "الشريف": ["Al Sharif"], "العلمي": ["Al Alami"], "عريقات": ["Erekat"],
    "شحادة": ["Shehadeh"], "عودة": ["Odeh"], "عوض": ["Awad"], "الصايغ": ["Al Sayegh"], "صايغ": ["Sayegh"], "سلامة": ["Salameh"],
    "حداد": ["Haddad"], "الحداد": ["Al Haddad"], "خوري": ["Khoury"], "الخوري": ["Al Khoury"], "نصار": ["Nassar"], "نجار": ["Najjar"],
    "النجار": ["Al Najjar"], "عبيدات": ["Obeidat"], "الزعبي": ["Al Zoubi"], "المجالي": ["Al Majali"], "الفايز": ["Al Fayez"],
    "الطراونة": ["Al Tarawneh"], "العدوان": ["Al Adwan"], "الروسان": ["Al Rousan"], "القضاة": ["Al Qudah"], "الخصاونة": ["Al Khasawneh"],
    "الرفاعي": ["Al Rifai"], "البطاينة": ["Al Bataineh"], "المصري": ["Al Masri"], "الشامي": ["Al Shami"], "العراقي": ["Al Iraqi"],
    "اليمني": ["Al Yamani"], "المغربي": ["Al Maghribi"], "السوداني": ["Al Sudani"], "الكبيسي": ["Al Kubaisi"], "الجبوري": ["Al Jubouri"],
    "الدليمي": ["Al Dulaimi"], "العبيدي": ["Al Obaidi"], "الربيعي": ["Al Rubaie"], "السامرائي": ["Al Samarrai"], "الحربي": ["Al Harbi"],
    "العتيبي": ["Al Otaibi"], "القحطاني": ["Al Qahtani"], "الشمري": ["Al Shammari"], "الدوسري": ["Al Dosari"], "الغامدي": ["Al Ghamdi"],
    "الزهراني": ["Al Zahrani"], "المطيري": ["Al Mutairi"], "العنزي": ["Al Anazi"], "السبيعي": ["Al Subaie"], "الشهري": ["Al Shehri"],
    "العمري": ["Al Omari"], "المالكي": ["Al Maliki"], "الأحمدي": ["Al Ahmadi"], "الجهني": ["Al Juhani"], "السهلي": ["Al Sahli"],
    "اليامي": ["Al Yami"], "الحازمي": ["Al Hazmi"], "العسيري": ["Al Asiri"], "التميمي": ["Al Tamimi"], "الخطيب": ["Al Khatib"],
    "الأحمد": ["Al Ahmad"], "الطويل": ["Al Taweel"], "حمدان": ["Hamdan"], "منصور": ["Mansour"], "يوسف": ["Youssef"],
};

/** Words that are never a name on their own (common Arabic words that look like names). */
const STOPWORDS = new Set(["على", "من", "الى", "إلى", "في", "عن", "مع", "هل", "ما", "لا", "نعم", "هذا", "هذه", "ذلك", "تلك", "التي", "الذي",
    "كان", "قد", "لم", "لن", "كل", "بعد", "قبل", "عند", "حتى", "او", "أو", "ثم", "يا", "انا", "أنا", "هو", "هي", "نحن", "انت", "أنت", "هم", "اليوم", "بكرا",
    "بكرة", "غدا", "غداً", "الساعة", "يوم", "شهر", "سنة", "أريد", "اريد", "بدي", "ممكن", "لو", "سمحت", "شكرا", "شكراً", "مرحبا", "السلام", "عليكم",
    "مطار", "المطار", "فندق", "الفندق", "سيارة", "سيارات", "سائق", "حارس", "طلب", "مهمة", "عميل", "مزود", "شركة", "جديد", "جديدة", "اضف", "أضف", "انشئ", "أنشئ"]
    .map((word) => plainArabic(word)));

const fold = (word: string): string => foldArabic(word);
const toMap = (record: Record<string, string[]>): Map<string, string[]> => new Map(Object.entries(record).map(([key, value]) => [fold(key), value]));
const GIVEN = toMap(GIVEN_NAMES);
const FAMILY = toMap(FAMILY_NAMES);

const capitalise = (word: string): string => word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word;

const SUN_FREE: Record<string, string> = {
    "ب": "b", "ت": "t", "ث": "th", "ج": "j", "ح": "h", "خ": "kh", "د": "d", "ذ": "dh", "ر": "r", "ز": "z", "س": "s", "ش": "sh",
    "ص": "s", "ض": "d", "ط": "t", "ظ": "dh", "غ": "gh", "ف": "f", "ق": "q", "ك": "k", "ل": "l", "م": "m", "ن": "n", "ه": "h",
};

/**
 * One Arabic word spelt in English letters by rule (no list): consonants as
 * the common English spellings, long vowels from ا و ي, and a short "a"
 * where three consonants would otherwise meet.
 * @param word - One Arabic word.
 * @returns e.g. "خليل" → "Khalil".
 */
export const transliterateWord = (word: string): string => {
    const plain = plainArabic(westernDigits(word)).trim();
    if (!plain) return "";
    if (plain.length > 3 && /^(?:ال|آل)/.test(plain)) return `Al ${transliterateWord(plain.slice(2))}`;
    const letters = Array.from(plain);
    const units: Array<{ text: string; consonant: boolean; long?: boolean }> = [];
    letters.forEach((letter, index) => {
        const first = index === 0;
        const last = index === letters.length - 1;
        const prev = units[units.length - 1];
        let text = "";
        let consonant = false;
        let long = false;
        switch (letter) {
            case "ا": case "أ": text = "a"; long = !first; break;
            case "إ": text = "i"; break;
            case "آ": text = first ? "a" : "aa"; long = true; break;
            case "ء": break;
            case "ع": text = last ? "" : "a"; break;
            case "ة": case "ى": text = "a"; break;
            case "ئ": text = "i"; break;
            case "ؤ": text = "u"; break;
            case "و":
                if (first || (prev && !prev.consonant)) { text = "w"; consonant = true; } else text = last ? "o" : "ou";
                break;
            case "ي":
                if (first || (prev && !prev.consonant && !last)) { text = "y"; consonant = true; } else text = "i";
                break;
            default:
                text = SUN_FREE[letter] ?? "";
                consonant = !!text;
        }
        if (!text) return;
        if (consonant) {
            const n = units.length;
            // a short "a" after a first consonant ("خليل" → Kha-lil), and never three consonants in a row
            if (n === 1 && units[0].consonant) units.push({ text: "a", consonant: false });
            else if (n >= 2 && units[n - 1].consonant && units[n - 2].consonant) units.push({ text: "a", consonant: false });
            // C-ā-C + a last consonant is usually C-ā-C-i-C (راشد → Rashid, خالد → Khalid)
            else if (last && n >= 3 && units[n - 1].consonant && units[n - 2].long && units[n - 3].consonant) units.push({ text: "i", consonant: false });
        }
        units.push({ text, consonant, long });
    });
    return capitalise(units.map((unit) => unit.text).join("").replace(/aaa+/g, "aa"));
};

/** Every alternative spelling a list gives for one word ("الكعبي" → Al Kaabi, Al-Kaabi, Alkaabi, Kaabi). */
const familyVariants = (spellings: string[]): string[] => {
    const out = new Set<string>();
    spellings.forEach((spelling) => {
        out.add(spelling);
        const bare = spelling.replace(/^Al\s+/i, "");
        if (bare !== spelling) {
            out.add(`Al-${bare}`);
            out.add(`Al${bare.toLowerCase()}`);
            out.add(`El ${bare}`);
            out.add(bare);
        }
    });
    return Array.from(out);
};

export interface EnglishName {
    /** CRX's house spelling. */
    english: string;
    /** Every word came from the lists (false = spelt by rule: show "check spelling"). */
    exact: boolean;
    /** Other common spellings of the whole name (house spelling first), at most 12. */
    variants: string[];
}

/** One word of a name: its spellings, and whether the lists knew it. */
const nameWord = (word: string): { spellings: string[]; known: boolean } => {
    const key = fold(word);
    const given = GIVEN.get(key);
    if (given) return { spellings: given, known: true };
    const family = FAMILY.get(key);
    if (family) return { spellings: familyVariants(family), known: true };
    // عبدالكريم, عبد الكريم → Abdulkarim
    const abd = /^عبد\s?ال(.+)$/.exec(key);
    if (abd) {
        const rest = GIVEN.get(fold(abd[1]))?.[0] ?? transliterateWord(abd[1]);
        return { spellings: [`Abdul${rest.toLowerCase()}`, `Abdel${rest.toLowerCase()}`, `Abdul ${rest}`], known: GIVEN.has(fold(abd[1])) };
    }
    // a family name with the article that isn't listed: Al + the word
    if (/^ال.{2,}$/.test(key)) {
        const rest = GIVEN.get(key.slice(2))?.[0] ?? transliterateWord(key.slice(2));
        return { spellings: familyVariants([`Al ${rest}`]), known: GIVEN.has(key.slice(2)) };
    }
    return { spellings: [transliterateWord(word)], known: false };
};

/**
 * A person's name written in Arabic, in English letters (house spelling).
 * Joins عبد + its word, keeps bin / bint / Abu / Al as particles.
 * @param text - "راشد بن سعيد الكعبي".
 * @returns The English name, whether every word was on the lists, and spelling variants.
 */
export const arabicNameToEnglish = (text: string): EnglishName => {
    const words = plainArabic(westernDigits(text)).replace(/[،,]+/g, " ").split(/\s+/).filter(Boolean);
    const parts: Array<{ spellings: string[]; known: boolean }> = [];
    for (let i = 0; i < words.length; i++) {
        const word = words[i];
        const key = fold(word);
        const next = words[i + 1];
        if (key === "عبد" && next) {
            parts.push(nameWord(`عبد${next}`));
            i++;
        } else if ((key === "ابو") && next) {
            const after = nameWord(next);
            parts.push({ spellings: after.spellings.map((spelling) => `Abu ${spelling}`), known: after.known });
            i++;
        } else if (key === "ال" && next) {
            const after = nameWord(next);
            parts.push({ spellings: after.spellings.map((spelling) => `Al ${spelling}`), known: after.known });
            i++;
        } else if (key === "بن" || key === "ابن") {
            parts.push({ spellings: ["bin"], known: true });
        } else if (key === "بنت") {
            parts.push({ spellings: ["bint"], known: true });
        } else if (ARABIC_LETTER.test(word)) {
            parts.push(nameWord(word));
        } else {
            parts.push({ spellings: [word], known: true });
        }
    }
    if (!parts.length) return { english: "", exact: false, variants: [] };
    const english = parts.map((part) => part.spellings[0]).join(" ").replace(/\s+/g, " ").trim();
    // a few combinations of the words' spellings (house spelling first)
    let variants = [""];
    parts.forEach((part) => {
        variants = variants.flatMap((start) => part.spellings.slice(0, 4).map((spelling) => `${start} ${spelling}`.trim())).slice(0, 24);
    });
    return { english, exact: parts.every((part) => part.known), variants: Array.from(new Set([english, ...variants])).slice(0, 12) };
};

// --- companies -------------------------------------------------------------

type CompanyRole = "suffix" | "kind" | "descriptor" | "brand";

/** Company words: Arabic phrase → [English, role]. Longest phrases are tried first. */
const COMPANY_WORDS: Array<[string, string, CompanyRole]> = [
    ["ش.ذ.م.م", "LLC", "suffix"], ["ذ.م.م", "LLC", "suffix"], ["ذ م م", "LLC", "suffix"], ["ش.م.م", "LLC", "suffix"], ["م.م.ح", "FZE", "suffix"],
    ["ش.م.ع", "PJSC", "suffix"], ["ش.م.خ", "PSC", "suffix"], ["المحدودة", "Ltd", "suffix"], ["محدودة", "Ltd", "suffix"], ["وشركاه", "& Partners", "suffix"],
    ["و شركاه", "& Partners", "suffix"], ["القابضة", "Holding", "suffix"],
    ["شركة", "Company", "kind"], ["مؤسسة", "Est.", "kind"], ["مجموعة", "Group", "kind"], ["مكتب", "Office", "kind"],
    ["للخدمات الأمنية", "Security Services", "descriptor"], ["الخدمات الأمنية", "Security Services", "descriptor"], ["خدمات أمنية", "Security Services", "descriptor"],
    ["للحراسات الأمنية", "Security Guarding", "descriptor"], ["الحراسات الأمنية", "Security Guarding", "descriptor"], ["حراسات أمنية", "Security Guarding", "descriptor"],
    ["للخدمات اللوجستية", "Logistics Services", "descriptor"], ["الخدمات اللوجستية", "Logistics Services", "descriptor"],
    ["للسياحة والسفر", "Travel & Tourism", "descriptor"], ["لتأجير السيارات", "Car Rental", "descriptor"], ["تأجير السيارات", "Car Rental", "descriptor"],
    ["للنقل البري", "Land Transport", "descriptor"], ["النقل البري", "Land Transport", "descriptor"], ["الشرق الأوسط", "Middle East", "brand"],
    ["للحراسات", "Guarding", "descriptor"], ["الحراسات", "Guarding", "descriptor"], ["حراسات", "Guarding", "descriptor"], ["للحراسة", "Guarding", "descriptor"],
    ["الحراسة", "Guarding", "descriptor"], ["للحماية", "Protection", "descriptor"], ["الحماية", "Protection", "descriptor"], ["حماية", "Protection", "descriptor"],
    ["للأمن", "Security", "descriptor"], ["الأمن", "Security", "descriptor"], ["الأمنية", "Security", "descriptor"], ["أمنية", "Security", "descriptor"],
    ["للخدمات", "Services", "descriptor"], ["الخدمات", "Services", "descriptor"], ["خدمات", "Services", "descriptor"], ["للنقل", "Transport", "descriptor"],
    ["النقل", "Transport", "descriptor"], ["للسياحة", "Tourism", "descriptor"], ["السياحة", "Tourism", "descriptor"], ["والسفر", "& Travel", "descriptor"],
    ["للسفر", "Travel", "descriptor"], ["السفر", "Travel", "descriptor"], ["للتجارة", "Trading", "descriptor"], ["التجارة", "Trading", "descriptor"],
    ["التجارية", "Trading", "descriptor"], ["للمقاولات", "Contracting", "descriptor"], ["المقاولات", "Contracting", "descriptor"],
    ["للاستشارات", "Consulting", "descriptor"], ["الاستشارات", "Consulting", "descriptor"], ["استشارات", "Consulting", "descriptor"],
    ["تأجير", "Rental", "descriptor"], ["السيارات", "Cars", "descriptor"], ["الليموزين", "Limousine", "descriptor"], ["ليموزين", "Limousine", "descriptor"],
    ["اللوجستية", "Logistics", "descriptor"], ["للتدريب", "Training", "descriptor"], ["التدريب", "Training", "descriptor"], ["للتكنولوجيا", "Technology", "descriptor"],
    ["للاتصالات", "Communications", "descriptor"], ["للطيران", "Aviation", "descriptor"], ["الطيران", "Aviation", "descriptor"],
    ["العامة", "General", "descriptor"], ["الدولية", "International", "descriptor"], ["دولية", "International", "descriptor"], ["العالمية", "Global", "descriptor"],
    ["الوطنية", "National", "brand"], ["المتحدة", "United", "brand"], ["العربية", "Arabian", "brand"], ["الخليج", "Gulf", "brand"], ["الخليجية", "Gulf", "brand"],
    ["درع", "Shield", "brand"], ["النخبة", "Elite", "brand"], ["الصقر", "Falcon", "brand"], ["صقر", "Falcon", "brand"], ["النسر", "Eagle", "brand"],
    ["النجمة", "Star", "brand"], ["نجمة", "Star", "brand"], ["الذهبية", "Golden", "brand"], ["الذهبي", "Golden", "brand"], ["الملكية", "Royal", "brand"],
    ["الحديثة", "Modern", "brand"], ["المتقدمة", "Advanced", "brand"], ["الذكية", "Smart", "brand"],
];
const COMPANY_INDEX = COMPANY_WORDS
    .map(([arabic, english, role]) => ({ words: fold(arabic).replace(/\s*\.\s*/g, ".").split(" "), english, role }))
    .sort((a, b) => b.words.length - a.words.length);

export interface EnglishCompany {
    english: string;
    exact: boolean;
}

/**
 * A company name written in Arabic, in English: the proper name spelt in
 * English letters, trade words translated ("للخدمات الأمنية" → Security
 * Services), legal forms as suffixes (ذ.م.م → LLC). A name made only of
 * translatable words keeps English word order ("درع الخليج" → Gulf Shield).
 * @param text - The Arabic company name.
 */
export const arabicCompanyToEnglish = (text: string): EnglishCompany => {
    const words = fold(text).replace(/\s*\.\s*/g, ".").replace(/[،,()"“”]+/g, " ").split(/\s+/).filter(Boolean);
    const names: string[] = [];
    const brands: string[] = [];
    const descriptors: string[] = [];
    const suffixes: string[] = [];
    let kind: string | null = null;
    let known = true;
    for (let i = 0; i < words.length;) {
        const hit = COMPANY_INDEX.find((entry) => entry.words.every((word, offset) => words[i + offset] === word));
        if (hit) {
            if (hit.role === "suffix") suffixes.push(hit.english);
            else if (hit.role === "kind") kind = kind ?? hit.english;
            else if (hit.role === "descriptor") descriptors.push(hit.english);
            else brands.push(hit.english);
            i += hit.words.length;
            continue;
        }
        if (!ARABIC_LETTER.test(words[i])) {
            names.push(words[i]);
        } else {
            const spelt = nameWord(words[i]);
            names.push(spelt.spellings[0]);
            known = false;
        }
        i++;
    }
    // Arabic puts the noun first ("درع الخليج" = shield of the gulf): with no spelt name, English order flips it
    const brandPart = names.length ? brands : [...brands].reverse();
    const keepsKind = kind && kind !== "Company" && kind !== "Est." ? kind : null;
    const parts = [...names, ...brandPart, ...descriptors, ...(keepsKind ? [keepsKind] : []), ...suffixes];
    if (!descriptors.length && !suffixes.length && !keepsKind && kind === "Company") parts.push("Company");
    if (!descriptors.length && !suffixes.length && !keepsKind && kind === "Est.") parts.push("Est.");
    return { english: parts.join(" ").replace(/\s+/g, " ").trim(), exact: known };
};

// --- job titles ------------------------------------------------------------

const WHOLE_TITLES: Record<string, string> = {
    "المدير العام": "General Manager", "مدير عام": "General Manager", "المدير التنفيذي": "CEO", "الرئيس التنفيذي": "CEO",
    "المدير المالي": "Finance Manager", "مدير مالي": "Finance Manager", "مالك": "Owner", "صاحب الشركة": "Owner", "شريك": "Partner", "مؤسس": "Founder",
};
const ROLES: Array<[string, string]> = [
    ["نائب مدير", "Deputy Manager"], ["نائب المدير", "Deputy Manager"], ["مديرة", "Manager"], ["مدير", "Manager"], ["منسقة", "Coordinator"], ["منسق", "Coordinator"],
    ["مسؤولة", "Officer"], ["مسؤول", "Officer"], ["مشرفة", "Supervisor"], ["مشرف", "Supervisor"], ["رئيسة", "Head"], ["رئيس", "Head"],
    ["أخصائية", "Specialist"], ["أخصائي", "Specialist"], ["مندوبة", "Representative"], ["مندوب", "Representative"], ["مهندسة", "Engineer"], ["مهندس", "Engineer"],
    ["مستشارة", "Consultant"], ["مستشار", "Consultant"], ["محاسبة", "Accountant"], ["محاسب", "Accountant"], ["ضابط", "Officer"], ["حارس", "Guard"], ["سائق", "Driver"],
];
const DEPARTMENTS: Record<string, string> = {
    "العمليات": "Operations", "عمليات": "Operations", "المبيعات": "Sales", "مبيعات": "Sales", "المشتريات": "Procurement", "مشتريات": "Procurement",
    "الأمن": "Security", "أمن": "Security", "المشاريع": "Projects", "مشاريع": "Projects", "المالية": "Finance", "مالية": "Finance",
    "الموارد البشرية": "HR", "موارد بشرية": "HR", "التسويق": "Marketing", "تسويق": "Marketing", "الحسابات": "Accounts", "حسابات": "Accounts",
    "النقل": "Transport", "نقل": "Transport", "اللوجستيات": "Logistics", "لوجستيات": "Logistics", "الأسطول": "Fleet", "أسطول": "Fleet",
    "التدريب": "Training", "تدريب": "Training", "الجودة": "Quality", "جودة": "Quality", "الفرع": "Branch", "فرع": "Branch", "قسم": "Department", "القسم": "Department",
};
const WHOLE = new Map(Object.entries(WHOLE_TITLES).map(([key, value]) => [foldArabic(key), value]));
const ROLE_LIST = ROLES.map(([key, value]) => [foldArabic(key), value] as [string, string]).sort((a, b) => b[0].length - a[0].length);
const DEPARTMENT_MAP = new Map(Object.entries(DEPARTMENTS).map(([key, value]) => [foldArabic(key), value]));

/**
 * A job title written in Arabic, in English: "مدير العمليات" → Operations
 * Manager, "رئيس قسم المبيعات" → Head of Sales, "مسؤول" → Officer. Null
 * when the title isn't one of the common ones.
 * @param text - The Arabic title.
 */
export const arabicTitleToEnglish = (text: string): string | null => {
    const key = foldArabic(text);
    if (WHOLE.has(key)) return WHOLE.get(key)!;
    const role = ROLE_LIST.find(([arabic]) => key === arabic || key.startsWith(`${arabic} `));
    if (!role) return null;
    const rest = key.slice(role[0].length).trim().replace(/^(?:قسم|القسم)\s+/, "");
    const department = rest ? DEPARTMENT_MAP.get(rest) ?? null : null;
    if (rest && !department) return role[1];
    if (!department) return role[1];
    return role[1] === "Head" ? `Head of ${department}` : `${department} ${role[1]}`;
};

// --- places ----------------------------------------------------------------

const COUNTRY_NAMES: Record<string, string> = {
    "الإمارات": "United Arab Emirates", "الإمارات العربية المتحدة": "United Arab Emirates", "الامارات": "United Arab Emirates", "دولة الإمارات": "United Arab Emirates",
    "السعودية": "Saudi Arabia", "المملكة العربية السعودية": "Saudi Arabia", "المملكة": "Saudi Arabia", "قطر": "Qatar", "الكويت": "Kuwait", "البحرين": "Bahrain",
    "عمان": "Oman", "سلطنة عمان": "Oman", "الأردن": "Jordan", "فلسطين": "Palestine", "لبنان": "Lebanon", "سوريا": "Syria", "سورية": "Syria",
    "العراق": "Iraq", "مصر": "Egypt", "اليمن": "Yemen", "ليبيا": "Libya", "تونس": "Tunisia", "الجزائر": "Algeria", "المغرب": "Morocco",
    "السودان": "Sudan", "موريتانيا": "Mauritania", "الصومال": "Somalia", "جيبوتي": "Djibouti", "جزر القمر": "Comoros", "تركيا": "Turkey",
    "إيران": "Iran", "باكستان": "Pakistan", "الهند": "India", "أفغانستان": "Afghanistan", "بريطانيا": "United Kingdom", "المملكة المتحدة": "United Kingdom",
    "أمريكا": "United States", "امريكا": "United States", "الولايات المتحدة": "United States", "فرنسا": "France", "ألمانيا": "Germany", "إيطاليا": "Italy",
    "إسبانيا": "Spain", "روسيا": "Russia", "الصين": "China", "اليابان": "Japan", "كندا": "Canada", "أستراليا": "Australia", "سويسرا": "Switzerland",
    "هولندا": "Netherlands", "بلجيكا": "Belgium", "السويد": "Sweden", "النرويج": "Norway", "اليونان": "Greece", "قبرص": "Cyprus", "إثيوبيا": "Ethiopia",
    "كينيا": "Kenya", "نيجيريا": "Nigeria", "جنوب أفريقيا": "South Africa", "أوكرانيا": "Ukraine", "أذربيجان": "Azerbaijan", "جورجيا": "Georgia",
    "أرمينيا": "Armenia", "كازاخستان": "Kazakhstan", "أوزبكستان": "Uzbekistan", "إندونيسيا": "Indonesia", "ماليزيا": "Malaysia", "سنغافورة": "Singapore",
    "تايلاند": "Thailand", "الفلبين": "Philippines", "بنغلاديش": "Bangladesh", "سريلانكا": "Sri Lanka", "نيبال": "Nepal", "مالي": "Mali", "النيجر": "Niger",
    "تشاد": "Chad", "أوغندا": "Uganda", "تنزانيا": "Tanzania", "رواندا": "Rwanda", "الكونغو": "Congo", "إسرائيل": "Israel",
};

const CITY_NAMES: Record<string, string> = {
    "دبي": "Dubai", "أبوظبي": "Abu Dhabi", "أبو ظبي": "Abu Dhabi", "الشارقة": "Sharjah", "عجمان": "Ajman", "رأس الخيمة": "Ras Al Khaimah",
    "الفجيرة": "Fujairah", "أم القيوين": "Umm Al Quwain", "العين": "Al Ain", "الرياض": "Riyadh", "جدة": "Jeddah", "مكة": "Makkah", "مكة المكرمة": "Makkah",
    "المدينة المنورة": "Madinah", "الدمام": "Dammam", "الخبر": "Khobar", "الظهران": "Dhahran", "تبوك": "Tabuk", "أبها": "Abha", "الطائف": "Taif",
    "الدوحة": "Doha", "مدينة الكويت": "Kuwait City", "المنامة": "Manama", "مسقط": "Muscat", "صلالة": "Salalah", "صحار": "Sohar", "نزوى": "Nizwa",
    "عمّان": "Amman", "عمان": "Amman", "إربد": "Irbid", "اربد": "Irbid", "الزرقاء": "Zarqa", "العقبة": "Aqaba", "البحر الميت": "Dead Sea",
    "القدس": "Jerusalem", "رام الله": "Ramallah", "نابلس": "Nablus", "الخليل": "Hebron", "بيت لحم": "Bethlehem", "غزة": "Gaza", "أريحا": "Jericho",
    "جنين": "Jenin", "طولكرم": "Tulkarm", "قلقيلية": "Qalqilya", "بيروت": "Beirut", "طرابلس": "Tripoli", "صيدا": "Sidon", "دمشق": "Damascus",
    "حلب": "Aleppo", "حمص": "Homs", "اللاذقية": "Latakia", "بغداد": "Baghdad", "البصرة": "Basra", "أربيل": "Erbil", "اربيل": "Erbil", "الموصل": "Mosul",
    "النجف": "Najaf", "كربلاء": "Karbala", "السليمانية": "Sulaymaniyah", "كركوك": "Kirkuk", "القاهرة": "Cairo", "الإسكندرية": "Alexandria",
    "الجيزة": "Giza", "شرم الشيخ": "Sharm El Sheikh", "الغردقة": "Hurghada", "الأقصر": "Luxor", "أسوان": "Aswan", "صنعاء": "Sanaa", "عدن": "Aden",
    "بنغازي": "Benghazi", "الرباط": "Rabat", "الدار البيضاء": "Casablanca", "مراكش": "Marrakesh", "الخرطوم": "Khartoum", "إسطنبول": "Istanbul",
    "اسطنبول": "Istanbul", "أنقرة": "Ankara", "لندن": "London", "باريس": "Paris", "نيويورك": "New York", "واشنطن": "Washington", "جنيف": "Geneva",
    "برلين": "Berlin", "روما": "Rome", "مدريد": "Madrid", "موسكو": "Moscow", "طهران": "Tehran", "كراتشي": "Karachi", "إسلام آباد": "Islamabad",
    "نيودلهي": "New Delhi", "مومباي": "Mumbai", "نيروبي": "Nairobi", "أديس أبابا": "Addis Ababa", "مقديشو": "Mogadishu",
};
const COUNTRIES = new Map(Object.entries(COUNTRY_NAMES).map(([key, value]) => [fold(key), value]));
const CITIES = new Map(Object.entries(CITY_NAMES).map(([key, value]) => [fold(key), value]));

const lookPlace = (text: string, kind: "country" | "city" | "any"): string | null => {
    const key = fold(text).replace(/^(?:في|ب|من|الى|إلى)\s+/, "");
    const tries = [key, key.replace(/^(?:مدينة|دولة|سلطنة|مملكة|إمارة|امارة)\s+/, "")];
    for (const attempt of tries) {
        if (kind !== "country" && CITIES.has(attempt)) return CITIES.get(attempt)!;
        if (kind !== "city" && COUNTRIES.has(attempt)) return COUNTRIES.get(attempt)!;
    }
    // a one-letter prefix: "بدبي" (in Dubai), "ودبي"
    if (/^[بلوف]/.test(key) && key.length > 3) return lookPlace(key.slice(1), kind);
    return null;
};

const ENGLISH_PLACES = new Set([...Object.values(COUNTRY_NAMES), ...Object.values(CITY_NAMES), "UAE", "KSA", "Abu Dhabi"].map((name) => name.toLowerCase()));

/**
 * Whether a text is the English name of a country or city on the lists ("Muscat", "Oman", "Dubai").
 * @param text - A word or two.
 */
export const isKnownPlaceName = (text: string): boolean => ENGLISH_PLACES.has(String(text ?? "").trim().toLowerCase());

/**
 * A place written in Arabic, in English: "مسقط، عمان" → "Muscat, Oman";
 * "دبي" → "Dubai". Each comma part is looked up (city first, then country,
 * so the bare "عمان" after a city is Oman). Null when no part is known.
 * @param text - The place.
 * @param kind - "country" or "city" to prefer one list; "any" (default) tries both.
 */
export const arabicPlaceToEnglish = (text: string, kind: "country" | "city" | "any" = "any"): string | null => {
    const parts = plainArabic(westernDigits(text)).split(/\s*[،,\-–]\s*/).map((part) => part.trim()).filter(Boolean);
    if (!parts.length) return null;
    // one kind wanted: the first part that is one ("مسقط، عمان" as a country → Oman)
    if (kind !== "any") return parts.map((part) => lookPlace(part, kind)).find(Boolean) ?? null;
    const found = parts.map((part, index) => lookPlace(part, index === 0 ? "any" : "country"));
    if (found.every((place) => !place)) return null;
    return found.map((place, index) => place ?? transliterateWord(parts[index])).join(", ");
};

/**
 * A short value for a lookup (a place, a service, a name) in English: places
 * by the list, then a company or person name spelt by rule. Text without
 * Arabic comes back as it is.
 * @param text - What the AI or the person gave.
 * @param kind - What it is, when known.
 */
export const toEnglishLookup = (text: string, kind: "place" | "country" | "city" | "name" | "company" | "any" = "any"): string => {
    const value = westernDigits(text).trim();
    if (!hasArabic(value)) return value;
    if (kind === "place" || kind === "country" || kind === "city" || kind === "any") {
        const place = arabicPlaceToEnglish(value, kind === "country" ? "country" : kind === "city" ? "city" : "any");
        if (place) return place;
    }
    if (kind === "company" || (kind === "any" && COMPANY_INDEX.some((entry) => entry.words.length === 1 && fold(value).split(" ").includes(entry.words[0])))) {
        return arabicCompanyToEnglish(value).english;
    }
    return arabicNameToEnglish(value).english;
};

/**
 * Plain-words search text in English: Arabic places and listed names become
 * English, other Arabic words are left out (CRX searches English records, and
 * a guessed spelling would only narrow the search). English words stay.
 * @param text - "مهمات دبي الأسبوع القادم Client-A".
 * @returns e.g. "Dubai Client-A".
 */
export const englishSearchWords = (text: string): string => {
    const value = westernDigits(text).trim();
    if (!hasArabic(value)) return value;
    const words = value.split(/\s+/).filter(Boolean);
    const out: string[] = [];
    for (let i = 0; i < words.length; i++) {
        const word = words[i];
        if (!ARABIC_LETTER.test(word)) {
            out.push(word);
            continue;
        }
        const pair = words[i + 1] && ARABIC_LETTER.test(words[i + 1]) ? lookPlace(`${word} ${words[i + 1]}`, "any") : null;
        if (pair) {
            out.push(pair);
            i++;
            continue;
        }
        const place = lookPlace(word, "any");
        if (place) out.push(place);
        else if (isKnownArabicName(word)) out.push(nameWord(word).spellings[0]);
    }
    return out.join(" ").replace(/\s+/g, " ").trim();
};

// --- spelling skeletons (for finding a name across scripts) ------------------

const ARABIC_CLASS: Record<string, string> = {
    "ب": "B", "ت": "T", "ط": "T", "ث": "T", "د": "D", "ض": "D", "ذ": "D", "ظ": "D", "ز": "D", "س": "S", "ص": "S", "ش": "C", "ج": "J",
    "ح": "H", "ه": "H", "خ": "X", "غ": "G", "ف": "F", "ق": "K", "ك": "K", "ل": "L", "م": "M", "ن": "N", "ر": "R",
};
const PARTICLES_AR = new Set(["بن", "ابن", "بنت", "ال"]);

/**
 * The consonants an Arabic name is written with, as classes shared with
 * latinSkeleton (vowels, ع and the article dropped): محمد الكعبي → MHMDKB.
 * @param text - Arabic name.
 */
export const arabicSkeleton = (text: string): string =>
    fold(text).split(" ").filter((word) => word && !PARTICLES_AR.has(word)).map((word) => {
        const bare = word.length > 3 && word.startsWith("ال") ? word.slice(2) : word;
        const letters = Array.from(bare);
        return letters.map((letter, index) => (index === letters.length - 1 && letter === "ه" ? "" : ARABIC_CLASS[letter] ?? "")).join("");
    }).join("").replace(/(.)\1+/g, "$1");

const PARTICLES_LATIN = new Set(["al", "el", "bin", "ibn", "bint"]);

/**
 * The consonant classes of a name written in English letters (see
 * arabicSkeleton): Mohammed Al Kaabi → MHMDKB, Rashid → RCD.
 * @param text - Latin-script name.
 */
export const latinSkeleton = (text: string): string =>
    String(text ?? "").normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase()
        .split(/[^a-z]+/).filter((word) => word && !PARTICLES_LATIN.has(word))
        .map((word) => {
            const bare = /^(?:al|el)[^aeiou]/.test(word) && word.length >= 6 ? word.slice(2) : word;
            return bare
                .replace(/kh/g, "X").replace(/sh|ch/g, "C").replace(/gh/g, "G").replace(/th/g, "T").replace(/dh/g, "D").replace(/ph/g, "F").replace(/ck/g, "K")
                .replace(/([aeiou])h$/, "$1")
                .replace(/[bp]/g, "B").replace(/t/g, "T").replace(/[dz]/g, "D").replace(/s/g, "S").replace(/[jg]/g, "J").replace(/h/g, "H")
                .replace(/[kqcx]/g, "K").replace(/[fv]/g, "F").replace(/l/g, "L").replace(/m/g, "M").replace(/n/g, "N").replace(/r/g, "R")
                .replace(/[^A-Z]/g, "");
        }).join("").replace(/(.)\1+/g, "$1");

/**
 * Whether a lone Arabic word must never be read as a name (a common word —
 * على، من، مطار — or shorter than three letters).
 * @param word - One Arabic word.
 */
export const isArabicStopword = (word: string): boolean => {
    const plain = plainArabic(word).trim();
    return plain.length < 3 || STOPWORDS.has(plain) || STOPWORDS.has(plain.replace(/^[وبلف]/, ""));
};

/** Whether a lone Arabic word is a known given or family name (on the lists). */
export const isKnownArabicName = (word: string): boolean => {
    const key = fold(word);
    return GIVEN.has(key) || FAMILY.has(key);
};
